import { Button, Grid } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { updateProject } from '../../../actions/projectActions'
import { PART_UPDATE_RESET } from '../../../constants/partConstants'
import {
  PROJECT_CURRENT_STEP,
  PROJECT_DETAILS_REQUEST,
  PROJECT_UPDATE_RESET,
} from '../../../constants/projectConstants'
import Loader from '../../Loader/Loader'
import ToastMessage from '../../ToastMessage/ToastMessage'
import AddOnServices from './AddOnServices'
import DeliveryDetails from './DeliveryDetails'
import Request from './Request'
import TotalCost from './TotalCost'

const EstimatedCostContainer = styled.div`
  min-height: calc(100vh - 490px);

  .scrollable {
    max-height: calc(100vh - 180px);
    overflow-y: scroll;
  }

  .request {
    border-bottom: 1px solid #404040;
    padding: 38px 0;

    .summary-card {
      margin-top: 32px;
      margin-left: 32px;
      padding: 12px 16px;

      .header {
        border-bottom: 1px solid $light-gray;
        padding-bottom: 6px;
        padding-left: 0;
        padding-right: 0;
      }

      .MuiCardContent-root {
        padding: 16px 0;

        div {
          color: $gray;
          font-size: 14px;
        }

        .items {
          .MuiGrid-root {
            border-bottom: 1px solid $light-gray;
            padding: 9px 0;

            &:nth-of-type(2n) {
              padding-left: 20px;
            }
          }
        }
      }

      .thumbnail {
        background-position: center center;
        width: 100%;
        height: 100%;
        background-position: center center;
        background-repeat: no-repeat;
        background-size: contain;

        &.part {
          padding: 20px;
          transform: scale(60%, 60%);
        }
      }

      .has-thumbnail {
        width: 100%;
        height: 175px;
      }

      .no-thumbnail {
        background-color: #159ebe;
        background-size: contain;
        width: 100%;
        height: 100%;
        max-height: 175px;
      }
    }
  }

  .customer-info {
    font-size: 14px;

    .label {
      font-weight: bold;
      min-width: 85px;
    }
  }
`

const EstimatedCosts = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [submitted, setSubmitted] = useState(false)
  const [toast, setToast] = useState(false)
  const [partToast, setPartToast] = useState(false)

  const { loading, project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )

  const { success: successProjectUpdate } = useSelector(
    (state: RootStateOrAny) => state.projectUpdate
  )
  const { success: partUpdateSuccess } = useSelector(
    (state: RootStateOrAny) => state.partUpdate
  )
  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)
  const { currentStep } = useSelector(
    (state: RootStateOrAny) => state.projectStep
  )

  useEffect(() => {
    if (!userInfo) {
      history.push(`/login?redirect=${window.location.pathname}`)
    }

    if (successProjectUpdate) {
      dispatch({ type: PROJECT_UPDATE_RESET })

      if (submitted) {
        window.scrollTo(0, 0)
        history.push('/quote-submitted')
      }
    }

    if (partUpdateSuccess) {
      setPartToast(true)
      dispatch({ type: PART_UPDATE_RESET })
      dispatch({ type: PROJECT_DETAILS_REQUEST })
    }
  }, [
    userInfo,
    successProjectUpdate,
    dispatch,
    submitted,
    partUpdateSuccess,
    history,
  ])

  const handleBackClick = () => {
    dispatch({
      type: PROJECT_CURRENT_STEP,
      payload: currentStep === 5 ? 2 : 8,
    })
  }

  const handleSubmitForReview = () => {
    let projectClone = JSON.parse(JSON.stringify(project)) // deep cloning

    projectClone.status = 'Submitted for review'
    projectClone.event = {
      event: 'Submitted for review',
      user: userInfo._id,
    }

    setSubmitted(true)
    dispatch(updateProject(projectClone))
  }

  return (
    <EstimatedCostContainer className="estimated-cost">
      <Grid container spacing={4}>
        <Grid item xs={8}>
          <div
            className="flex space-between"
            style={{ borderBottom: '1px solid #404040', paddingBottom: 24 }}
          >
            <h5>
              Review request(s){' '}
              {userInfo.isAdmin &&
                project.userName &&
                `for ${project.userName}`}
            </h5>
          </div>

          <div className="scrollable">
            {project.requests &&
              project.requests.map((request: IRequest, index: number) => (
                //todo: update Request component to typescript
                <Request
                  request={request}
                  key={index}
                  index={index}
                  isRequestSummary={null}
                />
              ))}

            {project.deliveryDetails &&
              Object.keys(project.deliveryDetails).length !== 0 && (
                <DeliveryDetails />
              )}

            <AddOnServices />
          </div>
        </Grid>
        <Grid item xs={4}>
          <TotalCost />

          {currentStep === 5 ? (
            <div className="m-t-40">
              <Button
                className="full-width m-b-16"
                color="primary"
                variant="contained"
                onClick={() =>
                  dispatch({
                    type: PROJECT_CURRENT_STEP,
                    payload: 6,
                  })
                }
              >
                Complete request details
              </Button>
              <Button
                color="primary"
                variant="outlined"
                className="full-width"
                onClick={handleBackClick}
              >
                Add another part
              </Button>
            </div>
          ) : (
            <div className="m-t-40">
              <div className="flex space-between">
                <Button
                  color="primary"
                  variant="outlined"
                  className={`m-r-24 ${loading ? 'loading' : ''}`}
                  disabled={loading}
                  onClick={() =>
                    dispatch({
                      type: PROJECT_CURRENT_STEP,
                      payload: 8,
                    })
                  }
                >
                  Back
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSubmitForReview}
                  disabled={loading}
                  className={loading ? 'loading' : ''}
                >
                  {loading ? <Loader /> : 'Submit for review'}
                </Button>
              </div>
            </div>
          )}
        </Grid>
      </Grid>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Project updated</p>
            <p className="fs14 m-b-0">The project was successfully updated.</p>
          </>
        }
        open={toast}
        setOpen={setToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Part updated</p>
            <p className="fs14 m-b-0">The part was successfully updated.</p>
          </>
        }
        open={partToast}
        setOpen={setPartToast}
      />
    </EstimatedCostContainer>
  )
}

export default EstimatedCosts
